/**
 * /coverage — the permanent context-entry RESOLVER (lifecycle "entry";
 * docs/COVERAGE_ROUTE_CONTRACT.md, decision 2026-07-22).
 *
 * A lightweight resolver, never a second coverage implementation:
 * - Fetches SELECTION OPTIONS only — never coverage results, never totals.
 * - No complete context → explicit cycle/entity/boundary selection with
 *   "Coverage context incomplete". Nothing is preselected.
 * - Complete VALID context in the URL → validate, then redirect (replace)
 *   to the canonical /cycles/:cycleId/coverage, preserving
 *   framework/requirement/metric/lens.
 * - Invalid supplied context → typed notice + selection (no fallback).
 */
import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CircleDashed, Lock } from "lucide-react";
import {
  C, MONO, SANS, HEAD, Card, Label, Skeleton, ErrorBanner, Btn,
} from "../../components/esg/EsgKit";
import useCoverageContext, { ENTITY_ONLY_BOUNDARY, buildCoveragePath } from "./useCoverageContext";

const selectStyle = {
  padding: "7px 10px", borderRadius: 6, background: C.surface,
  border: `1px solid ${C.border}`, color: C.ink,
  fontFamily: MONO, fontSize: 12, minWidth: 220, display: "block",
};

export default function CoverageResolverPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ctx = useCoverageContext();

  const urlCycle = params.get("cycle") || "";
  const urlEntity = params.get("entity") || "";
  const urlBoundary = params.get("boundary") || "";

  // Explicit selections only — initialized from the URL, never from data.
  const [cycleId, setCycleId] = useState(urlCycle);
  const [entityId, setEntityId] = useState(urlEntity);
  const [boundaryId, setBoundaryId] = useState(urlBoundary);

  if (ctx.error?.status === 403) {
    return (
      <Shell>
        <Card style={{ maxWidth: 560, margin: "40px auto", textAlign: "center", padding: 28 }}
              data-testid="coverage-permission-denied">
          <Lock size={22} strokeWidth={1.5} color={C.destructive} style={{ marginBottom: 10 }} />
          <p style={{ fontFamily: SANS, fontSize: 13, color: C.body, margin: 0 }}>
            Your role does not grant access to the coverage context for this tenant.
          </p>
        </Card>
      </Shell>
    );
  }
  if (ctx.error) {
    return (
      <Shell>
        <div style={{ maxWidth: 560, margin: "40px auto" }}>
          <ErrorBanner message={`Could not load selection options: ${ctx.error.message}`} />
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Btn variant="secondary" onClick={() => window.location.reload()}>Retry</Btn>
          </div>
        </div>
      </Shell>
    );
  }
  if (ctx.loading) {
    return <Shell><div style={{ padding: 20 }} data-testid="context-loading"><Skeleton h={220} /></div></Shell>;
  }

  // Complete + valid context supplied via URL → straight to canonical (replace).
  const urlValidation = ctx.validate(urlCycle, urlEntity, urlBoundary);
  if (urlValidation.ok) {
    return <Navigate replace to={buildCoveragePath(urlCycle, params)} />;
  }

  const invalidSupplied = urlValidation.type !== "incomplete_context";
  const chosen = ctx.validate(cycleId, entityId, boundaryId);
  const boundaryOptions = [...(ctx.boundaries || []), ENTITY_ONLY_BOUNDARY];

  const open = () => {
    if (!chosen.ok) return;
    const next = new URLSearchParams(params);
    next.set("entity", entityId);
    next.set("boundary", boundaryId);
    next.delete("cycle");
    navigate(buildCoveragePath(cycleId, next), { replace: true });
  };

  return (
    <Shell>
      {invalidSupplied && (
        <Card style={{ maxWidth: 560, margin: "0 auto 18px", padding: 16 }}
              data-testid={`coverage-${urlValidation.type}`}>
          <p style={{ fontFamily: SANS, fontSize: 13, color: C.body, margin: 0,
                      display: "flex", gap: 8, alignItems: "flex-start" }}>
            <AlertTriangle size={14} strokeWidth={1.5} color={C.destructive}
                           style={{ marginTop: 2, flexShrink: 0 }} />
            <span>
              The link supplied an unknown {urlValidation.type.replace("invalid_", "")}
              {" "}(<span style={{ fontFamily: MONO }}>{urlValidation.id}</span>).
              No fallback is applied — choose a valid context below.
            </span>
          </p>
        </Card>
      )}

      <Card style={{ maxWidth: 560, margin: "0 auto", padding: 24 }}
            data-testid="coverage-context-incomplete">
        <p style={{ fontFamily: SANS, fontSize: 13, color: C.body, margin: "0 0 16px",
                    display: "flex", gap: 8, alignItems: "flex-start" }}>
          <CircleDashed size={14} strokeWidth={1.5} color={C.muted}
                        style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            <strong style={{ color: C.ink }}>Coverage context incomplete.</strong>{" "}
            Coverage is computed for an explicit reporting cycle, entity, and
            boundary — nothing is assumed on your behalf.
          </span>
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <Label>Reporting cycle</Label>
            <select value={cycleId} onChange={(e) => setCycleId(e.target.value)}
                    aria-label="Reporting cycle" data-testid="cycle-select" style={selectStyle}>
              <option value="">Select a reporting cycle…</option>
              {(ctx.cycles || []).map((c) => (
                <option key={c.cycle_id} value={c.cycle_id}>{c.label || c.cycle_id}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Entity</Label>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)}
                    aria-label="Entity" data-testid="entity-select" style={selectStyle}>
              <option value="">Select an entity…</option>
              {(ctx.entities || []).map((e) => (
                <option key={e.entity_id} value={e.entity_id}>{e.name || e.entity_id}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Reporting boundary</Label>
            <select value={boundaryId} onChange={(e) => setBoundaryId(e.target.value)}
                    aria-label="Reporting boundary" data-testid="boundary-select" style={selectStyle}>
              <option value="">Select a boundary…</option>
              {boundaryOptions.map((b) => (
                <option key={b.boundary_id} value={b.boundary_id}>
                  {b.label || `${b.boundary_type} · ${b.boundary_id} v${b.version}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Btn variant="primary" disabled={!chosen.ok} onClick={open}
                 data-testid="open-coverage-btn">
              Open coverage
            </Btn>
          </div>
        </div>
      </Card>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "28px 32px",
                  fontFamily: SANS, maxWidth: 1240, margin: "0 auto" }}>
      <header style={{ marginBottom: 24, textAlign: "center" }}>
        <h1 style={{ fontFamily: HEAD, fontSize: 26, fontWeight: 700, color: C.ink,
                     letterSpacing: "-0.02em", margin: 0, textWrap: "balance" }}>
          Evidence Coverage
        </h1>
        <p style={{ fontFamily: SANS, fontSize: 13, color: C.body, margin: "6px 0 0" }}>
          Select the reporting context to open the coverage map.
        </p>
      </header>
      {children}
    </div>
  );
}
